import { firebaseShared } from "@shared/firebase.js";
import * as firebase from "@shared/firebase.js";
import * as environment from "@shared/environment.js";
import { debugDesc } from "@shared/utility.js";
import { dlog } from "@shared/utility.js";
import { t } from "@shared/i18n.js";

var collections = [];

function featuredCollWithID(id) {
    return collections.find(collection => collection.id === id) || null;
}

// The Firestore data is structured like so:
//  fetured-colls-*              -> collection
//      colls                    -> document
//          array                -> array
//              { coll 1 }
//              { coll 2 .. }
//
async function fetchFeaturedColls(force = false) {
    // Due to some weird firebase bug(?) where the auth token times out (I think in 30m), let's avoid trying to re-fetch feeds from firebase if we already have them.
    if (!force && collections.length > 0) {
        dlog("already fetched featured collections. bailing.");
        return;
    }

    try {
        // Keeping in mind the subtle distinction between firestore "collections" and our UI-presented "collections". 
        const firestoreCollName = environment.currentFeaturedCollectionsCollectionName();
        dlog(`fetchColls: start; collection: ${firestoreCollName}`);
        
        const collectionRef = firebase.collection(firebaseShared.db, firestoreCollName);
        dlog(`fetchColls: got collectionRef`);
        
        const snapshot = await firebaseShared.withRetry(() => firebase.getDocs(collectionRef));
        dlog(`fetchColls: got snapshot`);
        
        const tempCollections = [];
        
        snapshot.forEach(doc => {
            const array = doc.data().array; // yes this field is actually titled "array", and it should be the only thing in the document.
                    
            array.forEach(map => {
                // Check if collection has expired
                const currentTimestamp = Math.floor(Date.now() / 1000); // Current time in unix timestamp
                if (map.expireDate && map.expireDate < currentTimestamp) {
                    dlog(`ignoring expired collection: ${map.id} (expired: ${map.expireDate}, current: ${currentTimestamp})`);
                    return; // Skip this collection
                }
                
                const coll = {};
                coll.id = map.id;
                coll.title = map.title;
                coll.itemIDs = map.itemIDs;
                coll.isLive = map.isLive;
                coll.imageID = map.imageID;
                coll.expireDate = map.expireDate;
                tempCollections.push(coll);
            });

        });
        
        tempCollections.forEach(coll => {
            const localizedTitle = t(`featuredCollections.${coll.id}`, { defaultValue: "" });
            if (localizedTitle) coll.title = localizedTitle;
        });

        collections.length = 0;
        collections.push(...tempCollections);
        
        dlog("featuredCollections: " + debugDesc(collections));
    } catch (error) {
        console.warn("fetchFeaturedColls failed after retries:", error);
        return;
    }

}

// Get the prioritized collection ID from localStorage if it exists and is < 24 hours old
function getPrioritizedCollectionID() {
    try {
        const stored = localStorage.getItem("cyo4LandingPageSelectedCollection");
        if (!stored) {
            return null;
        }

        const data = JSON.parse(stored);
        if (!data || !data.id || !data.timestamp) {
            console.warn("Couldn't parse 'cyo4LandingPageSelectedCollection' data");
            return null;
        }

        // Check if timestamp is < 2 hours old. Div by 1000 to go from ms to s.
        const currentTimestamp = Math.floor(Date.now() / 1000);
        const age = currentTimestamp - data.timestamp;

        if (age > 2 * 3600) {
            dlog(`Prioritized collection expired: ${data.id} (age: ${age} seconds)`);
            return null;
        }

        dlog(`Using prioritized collection: ${data.id} (age: ${age} seconds)`);
        return data.id;
    } catch (error) {
        console.warn("Failed to parse cyo4LandingPageSelectedCollection from localStorage:", error);
        return null;
    }
}

// Get collections array with prioritized collection moved to first position (if valid)
function getOrderedCollections() {
    const prioritizedID = getPrioritizedCollectionID();

    if (!prioritizedID) {
        return collections;
    }

    // Find the prioritized collection
    const prioritizedIndex = collections.findIndex(coll => coll.id === prioritizedID);

    if (prioritizedIndex === -1) {
        console.warn(`Prioritized collection ID "${prioritizedID}" not found in featured collections`);
        return collections;
    }

    // If already first, return as-is
    if (prioritizedIndex === 0) {
        return collections;
    }

    // Create new array with prioritized collection first, followed by others
    const orderedCollections = [
        collections[prioritizedIndex],
        ...collections.slice(0, prioritizedIndex),
        ...collections.slice(prioritizedIndex + 1)
    ];

    return orderedCollections;
}

export { fetchFeaturedColls, collections, featuredCollWithID, getPrioritizedCollectionID, getOrderedCollections };
